import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { setActiveToken } from '../services/api';

interface User {
  id: number;
  name: string;
  email: string;
  // 'admin' (full dashboard) or 'counter' (NOKE 門市人員). Absent on a session
  // stored before roles existed — treat as 'admin', same as the backend does.
  role?: string;
}

interface AuthContextType {
  // User authentication
  currentUser: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (name: string, email: string, password: string, secretCode: string, role?: string) => Promise<void>;
  logout: () => Promise<void>;
  
  // E-commerce platforms
  shopeeAuthenticated: boolean;
  shopifyAuthenticated: boolean;
  getShopeeAuthUrl: () => Promise<string>;
  authenticateShopee: (code: string) => Promise<void>;
  disconnectShopee: () => Promise<void>;
  saveShopifyCredentials: (shopUrl: string, accessToken: string) => Promise<void>;
  disconnectShopify: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  // User authentication defaults
  currentUser: null,
  isAuthenticated: false,
  loading: true,
  login: async () => {},
  register: async () => {},
  logout: async () => {},
  
  // E-commerce platform defaults
  shopeeAuthenticated: false,
  shopifyAuthenticated: false,
  getShopeeAuthUrl: async () => '',
  authenticateShopee: async () => {},
  disconnectShopee: async () => {},
  saveShopifyCredentials: async () => {},
  disconnectShopify: async () => {},
});

export const useAuth = () => useContext(AuthContext);

// Every localStorage key that makes up a session. Kept in one place so that
// login, logout and the cross-tab sync below can never drift apart.
const AUTH_STORAGE_KEYS = ['authToken', 'appAuthenticated', 'userId', 'userName', 'currentUser'];

const clearAuthStorage = () => AUTH_STORAGE_KEYS.forEach((k) => localStorage.removeItem(k));

// Last-known user, so a tab can render a name immediately on mount instead of
// waiting for /api/auth/me. Always re-confirmed against the server.
const readStoredUser = (): User | null => {
  try {
    const raw = localStorage.getItem('currentUser');
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // User authentication state
  const [currentUser, setCurrentUser] = useState<User | null>(readStoredUser);
  const [token, setToken] = useState<string | null>(localStorage.getItem('authToken'));
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(!!token);
  const [loading, setLoading] = useState<boolean>(true);

  // E-commerce platforms state
  const [shopeeAuthenticated, setShopeeAuthenticated] = useState<boolean>(false);
  const [shopifyAuthenticated, setShopifyAuthenticated] = useState<boolean>(false);

  // Single writer for this tab's token. React state (what we render) and the api
  // client's in-memory token (who we act as) must never disagree — that gap IS
  // the two-accounts-on-one-machine bug.
  const applyToken = (next: string | null) => {
    setActiveToken(next);
    setToken(next);
  };

  useEffect(() => {
    const initAuth = async () => {
      await verifyToken(localStorage.getItem('authToken'));

      // Routing is unblocked as soon as the identity is known. The Shopee check
      // is a platform-status nicety and must NOT gate login — awaiting it here
      // used to leave the whole app on "Loading..." whenever that call was slow.
      setLoading(false);

      // verifyToken has just refreshed the stored user from /api/auth/me.
      // Limited accounts (門市人員) have no platform screens and would only get
      // a 403 here, so don't ask.
      const role = readStoredUser()?.role;
      if (role !== 'counter') {
        checkPlatformStatus();
      }
    };

    initAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-tab session sync.
  //
  // localStorage is shared by every tab on this origin, but each tab's React
  // state is initialised once at mount. Without this listener a tab keeps
  // rendering — and acting as — a user who has since been replaced on this
  // machine, which is how 員工績效 ended up attributed to whoever logged in last.
  //
  // `storage` only fires in the OTHER tabs, never the one that made the change.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      // e.key === null means localStorage.clear() — treat as a session change.
      if (e.key !== null && e.key !== 'authToken') return;

      const next = localStorage.getItem('authToken');
      if (next === token) return;

      if (!next) {
        // Another tab logged out. Drop this tab's session too; ProtectedRoute
        // sends it to /auth on the next render.
        applyToken(null);
        setCurrentUser(null);
        setIsAuthenticated(false);
        return;
      }

      // Another tab logged in, possibly as a different user. Reload rather than
      // swapping identity in place: pages hold user-scoped data in their own
      // state, and rendering one user's data under another's session is exactly
      // the bug this listener exists to kill.
      window.location.reload();
    };

    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [token]);

  // Verify a token against the server. Returns true when the session is usable.
  const verifyToken = async (candidate: string | null): Promise<boolean> => {
    if (!candidate) {
      applyToken(null);
      setCurrentUser(null);
      setIsAuthenticated(false);
      return false;
    }

    try {
      const response = await api.get('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${candidate}`
        }
      });

      applyToken(candidate);
      setCurrentUser(response.data.user);
      localStorage.setItem('currentUser', JSON.stringify(response.data.user));
      setIsAuthenticated(true);
      return true;
    } catch (error: any) {
      const status = error?.response?.status;

      if (status === 401 || status === 403) {
        clearAuthStorage();
        applyToken(null);
        setCurrentUser(null);
        setIsAuthenticated(false);
        return false;
      }

      // Network error, timeout or 5xx. Do NOT clear the token: localStorage is
      // shared, so wiping it here would log out every other open tab over a
      // transient blip. Keep the session and let the next request re-check.
      console.error('Token verification failed (transient — keeping session):', error);
      applyToken(candidate);
      setIsAuthenticated(true);
      return true;
    }
  };

  // Check e-commerce platform statuses
  const checkPlatformStatus = async () => {
    try {
      // Bounded: this runs in the background, but a hung request would leave the
      // connection open for the life of the tab.
      const shopeeResponse = await api.get('/api/shopee/auth/check', { timeout: 10000 });
      setShopeeAuthenticated(shopeeResponse.data.authenticated);

      // Shopify store closed 2026-07 — no live check, always unauthenticated
      setShopifyAuthenticated(false);
    } catch (error) {
      console.error('Error checking platform status:', error);
    }
  };

  // User authentication methods
  const login = async (email: string, password: string) => {
    try {
      const response = await api.post('/api/auth/login', { email, password });
      
      if (response.status === 200) {
        const { token, user } = response.data;
        
        // Current implementation
        localStorage.setItem('authToken', token);
        localStorage.setItem('appAuthenticated', 'true');
        
        // Add these lines to store user information
        localStorage.setItem('userId', user.id.toString());
        localStorage.setItem('userName', user.name);
        localStorage.setItem('currentUser', JSON.stringify(user));
        
        applyToken(token);
        setCurrentUser(user);
        setIsAuthenticated(true);
      } else {
        throw new Error(response.data.message || 'Login failed');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(error.response?.data?.message || 'Login failed. Please try again.');
    }
  };

  const register = async (name: string, email: string, password: string, secretCode: string, role?: string) => {
    try {
      // `role` is the account type the form asked for. The SERVER decides the real
      // role from which secret code was used and rejects a mismatch — this is only
      // sent so that mismatch can be reported clearly.
      const response = await api.post('/api/auth/register', { name, email, password, secretCode, role });
      
      if (response.status === 201) {
        const { token, user } = response.data;
        
        // Current implementation
        localStorage.setItem('authToken', token);
        localStorage.setItem('appAuthenticated', 'true');
        
        // Add these lines to store user information
        localStorage.setItem('userId', user.id.toString());
        localStorage.setItem('userName', user.name);
        localStorage.setItem('currentUser', JSON.stringify(user));
        
        applyToken(token);
        setCurrentUser(user);
        setIsAuthenticated(true);
      } else {
        throw new Error(response.data.message || 'Registration failed');
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      throw new Error(error.response?.data?.message || 'Registration failed. Please try again.');
    }
  };

  const logout = async () => {
    try {
      // Clean up local storage. Other tabs pick this up via the `storage`
      // listener above and drop their session too.
      clearAuthStorage();

      // Reset state
      applyToken(null);
      setCurrentUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Shopee methods
  const getShopeeAuthUrl = async (): Promise<string> => {
    try {
      const response = await api.get('/api/shopee/auth-url');
      return response.data.authUrl;
    } catch (error) {
      console.error('Failed to get Shopee auth URL:', error);
      throw error;
    }
  };

  const authenticateShopee = async (code: string) => {
    try {
      await api.post('/api/shopee/auth/token', { code });
      setShopeeAuthenticated(true);
    } catch (error) {
      console.error('Shopee authentication failed:', error);
      throw error;
    }
  };

  const disconnectShopee = async () => {
    try {
      await api.post('/api/shopee/auth/logout');
      setShopeeAuthenticated(false);
    } catch (error) {
      console.error('Shopee disconnect failed:', error);
      throw error;
    }
  };

  // Shopify methods
  const saveShopifyCredentials = async (shopUrl: string, accessToken: string) => {
    try {
      await api.post('/api/shopify/credentials', { shopUrl, accessToken });
      setShopifyAuthenticated(true);
    } catch (error) {
      console.error('Shopify credential save failed:', error);
      throw error;
    }
  };

  const disconnectShopify = async () => {
    try {
      await api.delete('/api/shopify/credentials');
      setShopifyAuthenticated(false);
    } catch (error) {
      console.error('Shopify disconnect failed:', error);
      throw error;
    }
  };

  const authContextValue: AuthContextType = {
    // User authentication
    currentUser,
    isAuthenticated,
    loading,
    login,
    register,
    logout,
    
    // E-commerce platforms
    shopeeAuthenticated,
    shopifyAuthenticated,
    getShopeeAuthUrl,
    authenticateShopee,
    disconnectShopee,
    saveShopifyCredentials,
    disconnectShopify,
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};